import { useCallback } from 'react';
import { useQuery, useQueryClient, UseQueryOptions } from 'react-query';
import { useCurrentOrganizationId } from './currentOrganization';
import { queryGet } from './fetch';

export interface StripeAddress {
  city: string | null;
  country: string | null;
  line1: string | null;
  line2: string | null;
  postal_code: string | null;
  state: string | null;
}

export interface PaymentIntent {
  id: string;
  object: 'payment_intent';
  amount: number;
  currency: string;
  description: string | null;
  livemode: boolean;
  payment_method: string | null | any;
  payment_method_types: Array<string>;
  receipt_email: string | null;
  client_secret: string;
  shipping: {
    address?: StripeAddress;
    carrier?: string | null;
    name?: string | null;
    phone?: string | null;
    tracking_number?: string | null;
  } | null;

  status: string;
  metadata: any;
}

export enum CreditOperationType {
  CREDIT = 'CREDIT',
  DEBIT = 'DEBIT',
}

export interface CreditBalance {
  id: string;
  amount: number;
  organizationId: string;
  expirationDate?: Date;
  updatedAt: Date;
}

export interface CreditOperation {
  id: string;
  amount: number;
  date: Date;
  description: string;
  type: CreditOperationType;
  organizationId: string;
  accountId?: string;
  sessionId?: string;
  adminEmail?: string;
  invoiceId?: string;
  previousBalance?: number;
  nextBalance?: number;
}

export interface SessionUsage {
  id: string;
  organizationId: string;
  sessionId: string;
  creditUsage: number;
  billedOrderUsage: number;
  lastOperationDate: Date;
  createdAt: Date;
}

export const useCreditBalance = (
  organizationId?: string,
  options?: UseQueryOptions<CreditBalance>,
) => {
  return useQuery<CreditBalance>(
    ['organizations', organizationId, 'credit-balance'],
    queryGet(`/v2/organizations/${organizationId}/credit-balance`),
    {
      enabled: !!organizationId,
      ...options,
    },
  );
};

export const useCreditOperations = (organizationId?: string) => {
  return useQuery<CreditOperation[]>(
    ['organizations', organizationId, 'credit-operations'],
    queryGet(`/v2/organizations/${organizationId}/credit-operations`),
    {
      enabled: !!organizationId,
    },
  );
};

export const usePendingCreditOperations = (organizationId?: string) => {
  return useQuery<CreditOperation[]>(
    ['organizations', organizationId, 'credit-operations', 'pendings'],
    queryGet(`/v2/organizations/${organizationId}/credit-operations/pendings`),
    {
      enabled: !!organizationId,
    },
  );
};

export const useClearCreditCache = () => {
  const queryClient = useQueryClient();
  const organizationId = useCurrentOrganizationId();
  return useCallback(() => {
    queryClient.invalidateQueries([
      'organizations',
      organizationId,
      'credit-operations',
    ]);
    queryClient.invalidateQueries([
      'organizations',
      organizationId,
      'credit-balance',
    ]);
  }, [organizationId, queryClient]);
};
